import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useMutation, useQueryClient } from 'react-query';

import { useParams } from 'react-router-dom';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';
import { UpdateStatusPriority } from '@models/UpdateStatus.model';
import { changeUpdateStatusByUpdate } from '@services/queries/updates';
import { useContext, useState } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';
import { useToast } from '@hooks';
import * as yup from 'yup';

import { useDispatch } from 'react-redux';
import { addUpdatedUpdate } from '@store/redux/features/updatesOptimistic';
import { AssigneeInput } from '@components/AssigneeInput/AssigneeInput';
import { DateInput } from '@components/DateInput/DateInput';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { patchDueDate } from '@services/queries/updates/patchDueDate/patchDueDate';
import { IUpdateRawQueryList } from '@models/Update.model';
import { addBackUpUpdatedUpdates } from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { getStableNow } from '@utils/getStableNow';
import { NeededUserInfo } from '@components/AssigneeInput/types';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Label,
  Divider,
  Item,
  Icon,
  AssigneeWrapper,
  LineDivider,
  Checkbox,
  Line,
  Confirm,
} from './PriorityDialog.styles';

interface IPriorityDialog {
  open: boolean;
  onClose: () => void;
  updateStatusId?: string;
  updateId?: string;
  closeOnFinish?: boolean;
  updateStatus: IUpdateRawQueryList;
}

export interface IPiorityForm {
  date: Date | null;
}

const schema = yup.object({
  date: yup.date(),
});

export const PriorityDialog = ({
  open,
  onClose,
  updateStatusId,
  updateStatus,
  updateId,
  closeOnFinish = true,
}: IPriorityDialog) => {
  const portalDiv = document.getElementById('portal');

  const { toast } = useToast();

  const DefaultAssigneeValue = () => {
    if (updateStatus.aName && updateStatus.assignee)
      return {
        name: updateStatus.aName,
        id: updateStatus.assignee,
      };
    return null;
  };

  const { studioId, updatesMtViewTime } = useContext(NavigationContext);
  const [assigneeId, setAssigneeId] = useState<NeededUserInfo | null>(
    DefaultAssigneeValue(),
  );
  const [priority, setPriority] = useState<UpdateStatusPriority | undefined>(
    updateStatus.priority ?? undefined,
  );
  const [dueDate, setDueDate] = useState<string | null>(
    updateStatus.dueDate?.toJSON() ?? null,
  );

  const queryClient = useQueryClient();

  const params = useParams<{ id: string; equipmentId: string }>();

  const dispatch = useDispatch();

  const reloadQueries = async () => {
    queryClient.invalidateQueries(['updateStatuses', updateId]);
    queryClient.invalidateQueries(['updateStatuses']);
    queryClient.invalidateQueries(['updatesMetrics']);
    queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
    queryClient.invalidateQueries([
      'equipments',
      params.equipmentId,
      'updateStatuses',
    ]);
  };

  const dueDateMutation = useMutation(patchDueDate, {
    onSuccess: async newUpdate => {
      if (closeOnFinish) {
        onClose();
      }
      toast.success('Update changed successfully');
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));
      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutationByUpdate = useMutation(changeUpdateStatusByUpdate, {
    onSuccess: async newUpdate => {
      if (closeOnFinish && !dueDate) {
        onClose();
      }
      toast.success('Update changed successfully');
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));
      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async newUpdate => {
      if (closeOnFinish && !dueDate) {
        onClose();
      }

      toast.success('Update changed successfully');
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));
      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const { control } = useForm<IPiorityForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      date: updateStatus.dueDate,
    },
  });

  const now = getStableNow();
  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Prioritize</Title>
              <Close
                onClick={() => {
                  onClose();
                  setPriority(undefined);
                  setAssigneeId(null);
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />

            <Label className="non-optional">Set priority as</Label>

            <Item
              onClick={() => {
                setPriority(UpdateStatusPriority.URGENT);
              }}
            >
              <Checkbox checked={priority === UpdateStatusPriority.URGENT} />
              <Icon>
                <BlueIcon icon="flag" color="#d14343" />
              </Icon>
              <Label>Urgent</Label>
            </Item>

            <Item
              onClick={() => {
                setPriority(UpdateStatusPriority.IMPORTANT);
              }}
            >
              <Checkbox checked={priority === UpdateStatusPriority.IMPORTANT} />
              <Icon>
                <BlueIcon icon="flag" color="#ff9900" />
              </Icon>
              <Label>Important</Label>
            </Item>

            <Item
              onClick={() => {
                setPriority(UpdateStatusPriority.NONCRITICAL);
              }}
            >
              <Checkbox
                checked={priority === UpdateStatusPriority.NONCRITICAL}
              />
              <Icon>
                <BlueIcon icon="flag" color="#52bd94" />
              </Icon>
              <Label>Non-critical</Label>
            </Item>

            <Item
              onClick={() => {
                setPriority(UpdateStatusPriority.UNCERTAIN);
              }}
            >
              <Checkbox checked={priority === UpdateStatusPriority.UNCERTAIN} />
              <Icon>
                <BlueIcon icon="flag" color="#c1c4d6" />
              </Icon>
              <Label>Uncertain</Label>
            </Item>
            <LineDivider />

            <Label className="optional">Assign to (Optional)</Label>
            <AssigneeWrapper>
              <AssigneeInput
                borderColor="#d8dae5"
                width={376}
                height={52}
                handleUserChange={(e: NeededUserInfo | null) => {
                  setAssigneeId(e);
                }}
                value={assigneeId}
              />
            </AssigneeWrapper>

            <Divider />

            <Label className="optional">Set Due Date (Optional)</Label>
            <Controller
              control={control}
              defaultValue={null}
              name="date"
              render={({ field: { onChange, value } }) => (
                <DateInput
                  placeholder="Choose..."
                  height={42}
                  width={374}
                  borderColor="#d8dae5"
                  value={value}
                  minDate={now.toISOString()}
                  handleDateChange={(e: any) => {
                    onChange(new Date(e));
                    setDueDate(e);
                  }}
                />
              )}
            />

            <Line>
              <Confirm
                disabled={priority === undefined}
                onClick={() => {
                  if (!mutation.isLoading && !mutationByUpdate.isLoading) {
                    if (updateStatusId) {
                      mutation.mutate({
                        updateStatusId,
                        priority,
                        assigneeId: assigneeId?.id,
                      });
                      dueDateMutation.mutate({
                        id: updateId,
                        dueDate: new Date(dueDate as string),
                        updateStatusId,
                      });
                    } else {
                      mutationByUpdate.mutate({
                        updateId,
                        studioId,
                        priority,
                        assigneeId: assigneeId?.id,
                      });
                      dueDateMutation.mutate({
                        id: updateId,
                        dueDate: new Date(dueDate as string),
                        updateStatusId,
                      });
                    }
                  }
                }}
              >
                Confirm
              </Confirm>
            </Line>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
